import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LifeBuoy } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Btn, Card, Field, Input, Select, Textarea } from "@/components/Ui";

export default function SupplierSupport() {
  const { user } = useAuth();
  const [topics, setTopics] = useState([]);
  const [f, setF] = useState({ topic: "technical", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);

  useEffect(() => {
    api.get("/support/topics").then((r) => setTopics(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/support/requests", {
        name: user?.name || "Supplier",
        email: user?.email,
        company: user?.company?.name || null,
        phone: user?.company?.phone || null,
        topic: f.topic,
        subject: f.subject,
        message: f.message,
      });
      setSent(data.reference);
      setF({ topic: "technical", subject: "", message: "" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="supplier-support">
      <Card>
        <div className="flex items-start gap-3">
          <LifeBuoy className="mt-0.5 h-5 w-5 shrink-0 text-[#147D78]" />
          <div>
            <h2 className="font-display text-lg font-bold tracking-tight text-[#102A43]">
              Raise a support ticket
            </h2>
            <p className="mt-1 text-sm text-[#667788]">
              Problem with an RFQ, a quotation, your subscription or your product data? Send it here
              and the ADIT team will get back to you.
            </p>
          </div>
        </div>

        {sent && (
          <div className="mt-5 rounded-[10px] border-l-[3px] border-[#13795B] bg-[#EAF7F0] px-4 py-3"
            data-testid="support-sent">
            <p className="text-sm font-semibold text-[#13795B]">
              Ticket {sent} logged — the ADIT team will get back to you.
            </p>
          </div>
        )}

        <form onSubmit={submit} className="mt-5 grid gap-4 sm:grid-cols-2">
          <Field label="What is it about? *">
            <Select data-testid="support-topic" value={f.topic} onChange={set("topic")}
              options={topics.map((t) => [t.value, t.label])} />
          </Field>
          <Field label="Subject *">
            <Input data-testid="support-subject" required minLength={3} value={f.subject}
              onChange={set("subject")} placeholder="Short summary of the issue" />
          </Field>
          <Field label="Describe the issue *" className="sm:col-span-2">
            <Textarea rows={6} data-testid="support-message" required minLength={10} value={f.message}
              onChange={set("message")}
              placeholder="What happened, which RFQ or part number is affected, and what you expected…" />
          </Field>
          <div className="sm:col-span-2">
            <Btn type="submit" data-testid="support-submit-btn" loading={busy}>Send ticket</Btn>
          </div>
        </form>
      </Card>
    </div>
  );
}
